import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { editDataStore, useSurveyInputStore, useTranslateStore } from "../../surveyStore";

import Section from "../../components/section/Section";
import ItemSelector from "../../components/question/question-preview/ItemSelector";
import AuthService from "../../../../../base/services/authentication.service";
import { LocalStorageService } from "../../../../../base/services/local-storage.service";
import { Button, Input, Modal, message, Spin, Empty, notification, Alert } from "antd";
import { appendSubmissionAnswers, getGeolocation, orderedItems } from "../../helpers";
import dayjs from "dayjs";
import https from "../../../../../base/utils/http";

import { SendOutlined } from "@ant-design/icons";
import SurveyConfirmation from "../SurveySubmissions/SurveyConfirmation";
import DraftList, { START_NEW_DRAFT } from "./PreviewComponents/DraftList";
import { Constants, isPublicSurveyRoute } from "../../../../../utils/constants";
import useOnlineStore from "../../../../../utils/useOnlineStore";
import PreviewBanner from "./PreviewComponents/PreviewBanner";
import PreviewLanguage from "./PreviewComponents/PreviewLanguage";
import { useFormik } from "formik";
import PreviewModal from "./PreviewComponents/PreviewModal";
import PreviewActions from "./PreviewComponents/PreviewActions";
import { InputType, SurveyStatusEnum, UserRolesEnum } from "../../../../../utils/enums";
import { withErrorBoundary } from "../../../../../components/ErrorBoundary/withErrorBoundary";
import { httpService } from "../../../../../base/services/httpService.service";
import usePrompt from "../../../../../utils/usePrompt";
import {
   saveDraft,
   loadDraft,
   getAllDraftsForSurvey,
   deleteDraft,
   db_drafts
} from "../../../../../storage/db";
import { useLiveQuery } from "dexie-react-hooks";

import { getSurveyTheme } from "../themes";
import { resolveSurveyColors, surveyColorVars } from "../surveyColors/surveyColors";
import { isFeedbackSurvey } from "../../../../workspace/survey/feedback/feedbackSurvey";
import { canRender } from "../../../../workspace/survey/feedback/feedbackPayload";

import "./preview.scss";
import "../surveyColors/surveyColors.scss";

const SurveyDataItems = React.memo(({ surveyData, hasErrors, submissionId }) => {
   // The survey's own level, in the one render order the whole app agrees on. The neighbour
   // lookups below index into this list, so they have to read the ordered one.
   const items = orderedItems(surveyData);

   return (
      <div className="preview-survey-section">
         {items.map((item, i) => {
            if (item.itemLabel === "Section") {
               return (
                  <Section
                     key={i}
                     hasErrors={hasErrors}
                     submissionId={submissionId}
                     className="survey-section"
                     surveyItem={item}
                  />
               );
            } else {
               const isPreviousItemSection = items[i - 1]?.itemLabel === "Section";
               const isPreviousItemInvisible = [
                  InputType.CALCULATE_QUESTION,
                  InputType.HIDDEN
               ].includes(items[i - 1]?.surveyItem?.questionType);
               const invisibleQuestions = [InputType.CALCULATE_QUESTION, InputType.HIDDEN].includes(
                  item?.surveyItem?.questionType
               );

               return (
                  <div
                     className={`preview-survey-section--questions_wrapper ${
                        (isPreviousItemSection && !invisibleQuestions) || isPreviousItemInvisible
                           ? "extra-padding"
                           : ""
                     } ${invisibleQuestions ? "p-0" : ""}`}
                     key={i}
                  >
                     <ItemSelector
                        hasErrors={hasErrors}
                        submissionId={submissionId}
                        props={item.surveyItem}
                     />
                  </div>
               );
            }
         })}
      </div>
   );
});

const getInputsState = () => {
   const { inputs } = useSurveyInputStore.getState();
   return inputs;
};

function Preview() {
   const { id, uuid } = useParams();
   const userId = AuthService.getUser()?.id;
   // A survey-specific theme (see ../themes) - null for every survey that has none.
   const surveyTheme = useMemo(() => getSurveyTheme(id), [id]);
   const ThemeRoot = surveyTheme?.Root ?? Fragment;
   const SurveyConfirmationView = surveyTheme?.Completion ?? SurveyConfirmation;
   // The survey's own colours (see ../surveyColors), read off the banner data - null keeps the
   // plain KAPsurvey look. A campaign theme above replaces the page, so it never reads these.
   const [savedColors, setSavedColors] = useState(null);
   const surveyColors = useMemo(() => resolveSurveyColors(savedColors), [savedColors]);
   const handleBannerLoad = useCallback(
      (bannerData) => setSavedColors(bannerData?.theme ?? null),
      []
   );
   const location = useLocation();
   const navigate = useNavigate();
   const submissionId = location.state;

   const componentRef = useRef();
   const isSubmitting = useRef(false);
   const [modalApi, modalContextHolder] = Modal.useModal();

   const isOnline = useOnlineStore((state) => state.isOnline);

   const getData = editDataStore((s) => s.fetchSurveyPreview);
   const surveyData = editDataStore((state) => state.surveyData);
   const loading = editDataStore((state) => state.isSurveyLoading);
   const surveyAlldata = editDataStore((state) => state.surveyAllData);
   const surveyBaseLanguage = editDataStore((state) => state.surveyBaseLanguage);
   const surveyFetchError = editDataStore((state) => state.surveyFetchError);

   const setDraftSurvey = useSurveyInputStore((state) => state.setDraftSurvey);
   const inputs = useSurveyInputStore((state) => state.inputs);
   const resetInputs = useSurveyInputStore((state) => state.resetInputs);
   const setInputs = useSurveyInputStore((state) => state.setInputs);

   const [isModalOpen, setIsModalOpen] = useState(false);
   const [isSaveAsDraft, setIsSaveAsDraft] = useState(false);
   const [selectedDraft, setSelectedDraft] = useState(null);
   const [load, setLoad] = useState(false);
   const [surveyStatusUpdated, setSurveyStatusUpdate] = useState(false);
   const [submittingAllDrafts, setSubmittingAllDrafts] = useState(false);

   const [hasErrors, setHasErrors] = useState(false);
   const { toLanguage } = useTranslateStore();
   const [startTimeEnd, setStartTimeEnd] = useState(null);

   const [searchParams] = useSearchParams();
   const taskId = searchParams.get("taskId");
   const receiverId = searchParams.get("receiverId");
   const collectionTypeParam = Number(searchParams.get("collectionType"));
   const dataCollectionType = Number.isInteger(collectionTypeParam) ? collectionTypeParam : 0;
   const [assignedTaskIdsForSurvey, setAssignedTaskIdsForSurvey] = useState([]);
   const [surveySubmittedSuccessfully, setSurveySubmittedSuccessfully] = useState("");

   const [hasSurveyExpired, setHasSurveyExpired] = useState();

   // indexedDB live queries
   const draftCount = useLiveQuery(() => db_drafts.where({ surveyId: id }).count());

   const draftFormik = useFormik({
      initialValues: {
         draftTitle: "",
         draftDesc: ""
      }
   });
   const { draftTitle, draftDesc } = draftFormik.values;
   const isEditingSurvey = surveyAlldata?.surveyStatusId === SurveyStatusEnum.Editing;

   const inputValues = useMemo(() => {
      const surveyQuestions = editDataStore.getState().surveyQuestions;

      return Object.values(inputs).filter((inputItem) => {
         // Inside a repeat group an input is named "<question>/<n>", one per instance, so the
         // bare name is what identifies the question. Matching on it keeps the rule below - never
         // submit a note, a calculation, a hidden question or anything carrying a default -
         // applying inside a group as well as outside it. A section counter is named
         // "<section>__counter", matches no question, and stays.
         const questionName = inputItem.name?.split("/")[0];
         const question = surveyQuestions.find((q) => q.name === questionName);

         if (
            question &&
            (question.default !== null ||
               [InputType.NOTE, InputType.CALCULATE_QUESTION, InputType.HIDDEN].includes(
                  question.questionType
               ))
         ) {
            return false;
         }

         return true;
      });
   }, [inputs]);

   usePrompt(
      "You have unsaved changes, are you sure you want to leave?",
      !isSubmitting.current && inputValues?.length !== 0
   );

   function clearLocalStorageKeys(keysToClear) {
      keysToClear.forEach((key) => {
         LocalStorageService.removeItem(key); // Remove only the specified keys
      });
   }

   // Explanation: this clears up localStorage keys,
   // which means will log out the user that is logged in in that browser,
   // in order to not submit the Public Survey with the account that is logged in currently
   if (isPublicSurveyRoute() && !taskId) {
      clearLocalStorageKeys([
         "user",
         "tokenExpire",
         "atoken",
         "rtoken",
         "organizationId",
         "organizationName",
         "roles"
      ]);
   }

   useEffect(() => {
      getAllDraftsForSurvey(id).then((drafts) => {
         const draftsLength = drafts.length;
         setIsModalOpen(draftsLength > 0);
      });
   }, [id]);

   // TODO: add navigation blocking for unsaved changes
   useEffect(() => {
      return () => {
         setInputs();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      const hasExpired = dayjs().isAfter(dayjs(surveyAlldata?.endDate), "day");
      setHasSurveyExpired(hasExpired);
   }, [surveyAlldata?.endDate]);

   useEffect(() => {
      if (!surveySubmittedSuccessfully) {
         getData({
            surveyId: id,
            languageId: toLanguage,
            taskId,
            receiverId
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id, toLanguage, surveySubmittedSuccessfully]);

   useEffect(() => {
      if (isOnline && taskId) {
         const params = new URLSearchParams({
            surveyId: id,
            userId: userId
         });

         httpService.get(
            `Task/GetAssignedTaskIdsForSurveyAndDataCollector?${params.toString()}`,
            (res) => {
               if (res.data?.length > 0) {
                  setAssignedTaskIdsForSurvey(res.data);
               }
            },
            (_error) => {
               console.log("_error:", _error);
            }
         );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOnline, taskId]);

   useEffect(() => {
      LocalStorageService.setItem("startTime", dayjs());
      setStartTimeEnd(dayjs());

      if (surveyAlldata.toggleLocation) {
         getGeolocation()
            .then((geolocation) => {
               LocalStorageService.setItem(`geolocation`, geolocation);
            })
            .catch((error) => {
               console.error("Error getting geolocation:", error);
               LocalStorageService.removeItem("geolocation");
            });
      }
   }, [surveyAlldata.toggleLocation]);

   const handleOpenDraftsModal = () => {
      setIsModalOpen(true);
   };

   const discardDraft = (draftId) => {
      deleteDraft(id, draftId);

      const draftSurveyId = [id, draftId];
      LocalStorageService.removeItem(`geolocation==${draftSurveyId}`);
      LocalStorageService.removeItem(`startTime==${draftSurveyId}`);
   };

   // `onSubmit` answers `false` when it refuses a submission. A draft is never on screen while it
   // is submitted, so this is the only thing that says which drafts stayed behind - submitting
   // several at once would otherwise look like it took them all.
   const reportSkippedDrafts = (drafts, results) => {
      const skipped = drafts.filter((_, index) => results[index] === false);

      if (skipped.length === 0) {
         return;
      }

      message.open({
         type: "error",
         content: `Not submitted, required answers are missing: ${skipped
            .map((draft) => draft.draftTitle || "Untitled draft")
            .join(", ")}. Open ${skipped.length === 1 ? "it" : "them"} to fill in what is missing.`
      });
   };

   const onSaveAsDraft = async () => {
      const inputs = getInputsState();
      const inputValues = Object.values(inputs).filter(
         (input) =>
            ![InputType.NOTE, InputType.CALCULATE_QUESTION, InputType.HIDDEN].includes(
               input.questionType
            )
      );

      setHasErrors(false);
      if (inputValues?.length === 0) {
         message.open({
            type: "error",
            content:
               "Error: Please provide at least one piece of information before saving a draft."
         });

         return;
      }

      const renderedElements = document.querySelectorAll("[data-id]");
      const renderedElementIds = [...renderedElements].map((item) => item.attributes[0].value);

      const draftId = selectedDraft
         ? selectedDraft
         : dayjs()
              .valueOf()
              .toString(36);
      await saveDraft({
         surveyId: id,
         draftId: draftId,
         draftTitle: draftTitle,
         draftDescription: draftDesc,
         renderedElementIds: renderedElementIds,
         formData: inputs
      });

      const draftSurveyId = [id, draftId];
      if (surveyAlldata.toggleLocation) {
         getGeolocation()
            .then((geolocation) => {
               LocalStorageService.setItem(`geolocation==${draftSurveyId}`, geolocation);
            })
            .catch((error) => {
               console.error("Error setting geolocation for draft: " + draftTitle, error);
               LocalStorageService.removeItem("geolocation");
            });
      }

      const surveyStartTime = LocalStorageService.getItem(`startTime`);
      const draftSurveyStartTime = LocalStorageService.getItem(`startTime==${draftSurveyId}`);
      if (!draftSurveyStartTime) {
         LocalStorageService.setItem(`startTime==${draftSurveyId}`, surveyStartTime);
      }

      message.open({
         type: "success",
         content: "Your answers have been saved in your local device"
      });
      LocalStorageService.setItem("startTime", dayjs());

      draftFormik.resetForm();
      setSelectedDraft(null);
      resetInputs();

      setIsSaveAsDraft(false);
   };

   const countDown = () => {
      let secondsToGo = 10;

      const handleDone = () => {
         clearInterval(timer);
         instance.destroy();
         window.location.reload();
      };

      const instance = modalApi.success({
         width: 600,
         onOk: handleDone,
         title: "Survey queued for submission.",
         content: `Queued surveys, except those marked as draft, are uploaded automatically, 
         in the background when Internet connection is available. 
         Surveys are kept in queue for 30 days from the time they were submitted.`,
         footer: (_, { OkBtn }) => (
            <>
               <Button type="text">{secondsToGo}</Button>
               <OkBtn />
            </>
         )
      });

      const timer = setInterval(() => {
         secondsToGo -= 1;
         instance.update({
            footer: (_, { OkBtn }) => (
               <>
                  <Button type="text">{secondsToGo}</Button>
                  <OkBtn />
               </>
            )
         });
      }, 1000);

      setTimeout(handleDone, secondsToGo * 1000);
   };

   const checkIfUUIDExists = (uuid) => {
      const submittedUUIDs = LocalStorageService.getItem("submittedUUIDs") || [];
      return submittedUUIDs.includes(uuid);
   };

   // Function to save UUID to localStorage
   const saveUUIDToLocalStorage = (uuid) => {
      const submittedUUIDs = LocalStorageService.getItem("submittedUUIDs") || [];
      LocalStorageService.setItem("submittedUUIDs", [...submittedUUIDs, uuid]);
   };

   const onSubmit = async (
      selectedDraft,
      inputs,
      isDraft,
      draftRenderedElementIds,
      currentTaskId
   ) => {
      isSubmitting.current = true;
      // Check if UUID exists in localStorage
      if (checkIfUUIDExists(uuid)) {
         message.open({
            type: "error",
            content: "Survey has already been submitted. You can't submit more than once."
         });
         setLoad(false);
         return;
      }
      setHasErrors(false);
      const renderedElements = document.querySelectorAll("[data-id]");
      const renderedElementIds = [...renderedElements]
         .map((item) => item.attributes[0].value)
         .filter((id) => id !== "goog-gt-tt"); // Remove google translate tooltip id
      const renderedConstraintMessages = document.querySelectorAll(".preview-question-constraint");
      const renderedConstraintMaxChoiceError = document.querySelectorAll(
         ".select_multiple-submission-type--options_error"
      );
      const geolocation = LocalStorageService.getItem(
         selectedDraft ? `geolocation==${[id, selectedDraft]}` : "geolocation"
      );
      const submissionData = {
         "submission.dataCollectionType": dataCollectionType,
         "submission.SubmissionStartTime": startTimeEnd,
         "submission.isAnonymous": !AuthService.getUser()?.id,
         userId: AuthService.getUser()?.id || "",
         surveyId: id,
         ...(taskId ? { TaskId: currentTaskId ? currentTaskId : taskId } : {}),
         ...(receiverId ? { MessageReceiverId: receiverId } : {}),
         ...(surveyAlldata.toggleLocation && geolocation
            ? {
                 "submission.geolocation.latitude": geolocation.latitude,
                 "submission.geolocation.longitude": geolocation.longitude
              }
            : {})
      };

      const formData = new FormData();
      for (const [key, value] of Object.entries(submissionData)) {
         formData.append(key, value);
      }

      const inputValues = Object.values(inputs);
      const elementIds = isDraft ? draftRenderedElementIds : renderedElementIds;
      const relevantErrors = useSurveyInputStore.getState().getErrors(elementIds, inputs);

      if (relevantErrors.length > 0 || inputValues.length === 0) {
         setLoad(false);

         // A draft is submitted from the drafts list without being rendered, so there is nothing
         // to mark or scroll to and this message would not say which of the drafts it is about.
         // The callers name the drafts they got `false` for instead.
         if (isDraft) {
            return false;
         }

         setHasErrors(true);

         const firstErrorElement = Array.from(renderedElements).find((el) =>
            relevantErrors.includes(el.attributes[0].value)
         );
         firstErrorElement?.scrollIntoView({ behavior: "smooth", block: "center" });

         message.open({
            type: "error",
            content:
               inputValues.length === 0
                  ? "Error: Please provide at least one piece of information before submitting."
                  : "Error: Please make sure you have filled all the required fields."
         });
         return false;
      }

      // Both of these read the survey that is on screen. A draft is submitted without being
      // rendered, so what they find belongs to whatever the enumerator has open - never to the
      // draft - and letting it block the draft made a bulk submit fail for no visible reason.
      if (
         !isDraft &&
         ((renderedConstraintMessages && renderedConstraintMessages.length > 0) ||
            (renderedConstraintMaxChoiceError && renderedConstraintMaxChoiceError.length > 0))
      ) {
         setTimeout(function() {
            setLoad(false);
         }, 500);

         if (renderedConstraintMaxChoiceError?.length > 0) {
            renderedConstraintMaxChoiceError[0].scrollIntoView({
               behavior: "smooth",
               block: "center"
            });
            return message.open({
               type: "error",
               content:
                  "Error: You have exceeded the maximum number of choices allowed. Please provide the correct answers."
            });
         }
         renderedConstraintMessages[0].scrollIntoView({ behavior: "smooth", block: "center" });

         return message.open({
            type: "error",
            content:
               "Error: Please check the constraints of the questions and provide the correct answers."
         });
      }

      // The store keys an input by exactly the string the renderer puts in `data-id`: the bare
      // question id outside a repeat, and "<survey>/<group>__repeat/<questionId>/<n>" for one
      // instance inside one. So the id is matched whole - taking `split("/")[0]` off a repeat
      // instance yielded the survey id, which is never a `data-id`, and dropped every answer
      // given inside a repeat group. Sections carry `data-xpath` and no `data-id` of their own,
      // so their repeat counters can never appear here; Section.jsx drops the counter of a group
      // its skip logic hides, which is what keeps a hidden group out of the payload.
      const renderedInputs = inputValues.filter(
         (input) =>
            input.questionType === InputType.SECTION || elementIds.includes(input.questionId)
      );

      appendSubmissionAnswers(formData, renderedInputs, elementIds);

      const timeout = 120000;
      return https
         .post("/DataCollection/PublishSurvey", formData, {
            headers: {
               "Content-Type": "multipart/form-data"
            },
            timeout
         })
         .then((response) => {
            if (response.data.isSuccess) {
               if (!Boolean(taskId) && !Boolean(receiverId)) {
                  message.open({
                     type: "success",
                     content: response.data.message
                  });
                  if (uuid && !uuid.includes("multi-use")) {
                     saveUUIDToLocalStorage(uuid);
                     // The saved uuid is what shows the completion page; a themed survey shows
                     // it straight away (below) instead of reloading into it.
                     if (!surveyTheme) {
                        setTimeout(() => window.location.reload(), 1500);
                     }
                  }
                  if (uuid === "multi-use") {
                     setSurveySubmittedSuccessfully("multi-use");
                  }

                  if (uuid === "multi-use-kiosk") {
                     setSurveySubmittedSuccessfully("multi-use-kiosk");
                     resetInputs();

                     setTimeout(() => {
                        setLoad(false);
                        setSurveySubmittedSuccessfully("");
                     }, 3000);
                  } else if (surveyTheme && uuid) {
                     // A themed survey stays on its completion page, single-use and multi-use
                     // link alike: a reload would take the page away after 1.5s.
                     setLoad(false);
                  } else {
                     setTimeout(() => {
                        setLoad(false);
                        window.location.reload();
                     }, 1500);
                  }
               }

               if (Boolean(taskId)) {
                  setSurveyStatusUpdate(true);
                  if (!currentTaskId) {
                     navigate("/mytasks");
                  }
               }

               if (Boolean(receiverId)) {
                  setSurveyStatusUpdate(true);
                  setTimeout(() => window.location.reload(), 1500);
               }

               if (selectedDraft) {
                  discardDraft(selectedDraft);
               }

               LocalStorageService.removeItem("startTime");
               LocalStorageService.removeItem("geolocation");
            } else {
               setHasErrors(true);
               setLoad(false);
            }

            return response;
         })
         .catch((err) => {
            if (isPublicSurveyRoute && !isOnline) {
               if (selectedDraft) {
                  discardDraft(selectedDraft);
               }

               LocalStorageService.removeItem(`startTime`);
               LocalStorageService.removeItem(`geolocation`);
               countDown();
            }
            if (isOnline) {
               isSubmitting.current = false;
               if (!err.response) {
                  setLoad(false);
                  notification.error({
                     message:
                        "This issue might be caused by a poor internet connection. Please save your submission as a draft and try resubmitting it once you have a more stable connection."
                  });
               } else {
                  const res = err.response.data;
                  notification.error({
                     message: (
                        <div>
                           <div>
                              {res.message ||
                                 res.title ||
                                 "Something went wrong! Please try again later."}
                           </div>
                           {Array.isArray(res?.errors) && (
                              <>
                                 <br />
                                 <ul>
                                    {res?.errors?.map((err) => (
                                       <li key={err}>{err}</li>
                                    ))}
                                 </ul>
                              </>
                           )}
                        </div>
                     )
                  });
               }
            }
            setLoad(false);

            return err;
         });
   };

   const handleSubmittingAllDrafts = () => {
      setSubmittingAllDrafts((prevState) => !prevState);
   };

   const shouldAddArabicClass = !toLanguage
      ? surveyBaseLanguage === Constants.ARABIC_LANGUAGE_ID
      : toLanguage === Constants.ARABIC_LANGUAGE_ID;

   const showSurveyActions =
      !submissionId &&
      !isEditingSurvey &&
      !AuthService.isAuthorized([UserRolesEnum.Guest]) &&
      !surveyFetchError;

   const actionProps = {
      seeDraftsProps: {
         onClick: handleOpenDraftsModal
      },
      saveDraftProps: {
         onClick: () => {
            if (selectedDraft) {
               onSaveAsDraft();
               return;
            }
            setIsSaveAsDraft(true);
         }
      },
      submitProps: {
         onClick: () => {
            const inputs = getInputsState();
            setLoad(true);
            onSubmit(selectedDraft, inputs);
         },
         loading: load
      },
      hideSubmitButton: !isOnline && (taskId || receiverId)
   };

   // A customer feedback survey is answered on its kiosk screen. Its share links and
   // campaign links point there, but a link sent before they did — a campaign message
   // with the standard shape — lands here, so it is handed over with its query string
   // (receiverId, collectionType) intact. An assignment's link (taskId) stays: the kiosk
   // has no task flow. A survey the kiosk can't render is sent back here by the kiosk,
   // so it is not handed over.
   if (
      uuid &&
      isPublicSurveyRoute() &&
      !taskId &&
      surveyAlldata?.id === id &&
      isFeedbackSurvey(surveyAlldata) &&
      canRender(surveyAlldata.surveyItems)
   ) {
      return <Navigate replace to={`/public-survey/feedback/${uuid}/${id}${location.search}`} />;
   }

   return (
      <ThemeRoot>
         {surveyTheme ? (
            <surveyTheme.Hero />
         ) : (
            <PreviewBanner loading={loading} onLoad={handleBannerLoad} />
         )}

         {loading ? (
            <div className="loading-container">
               <Spin
                  style={{
                     position: "fixed",
                     top: "50%",
                     left: "50%",
                     transform: "translate(-50%, -50%)"
                  }}
               />
            </div>
         ) : (
            <>
               <div
                  ref={componentRef}
                  className={
                     surveyTheme?.scopeClassName ?? (surveyColors ? "survey-colors" : undefined)
                  }
                  style={
                     surveyTheme
                        ? undefined
                        : surveyColors
                        ? surveyColorVars(surveyColors)
                        : {
                             backgroundColor: "#EEF2FF"
                          }
                  }
               >
                  {checkIfUUIDExists(uuid) || (surveyStatusUpdated && receiverId) ? (
                     <SurveyConfirmationView />
                  ) : !!surveySubmittedSuccessfully ? (
                     <SurveyConfirmationView
                        surveySubmittedSuccessfully={surveySubmittedSuccessfully}
                     />
                  ) : Boolean(surveyAlldata.id) &&
                    (taskId || receiverId) &&
                    !surveyAlldata?.canSubmitSurvey ? (
                     <SurveyConfirmationView isSurveySubmitted />
                  ) : (
                     <div className="survey-previewer">
                        {isEditingSurvey ? (
                           <Alert
                              prefixCls="survey-previewer--alert"
                              type="warning"
                              message="This is a preview of your survey draft."
                              description="Once you finish editing your survey, publish it and start collecting data"
                           />
                        ) : null}

                        {surveyTheme ? (
                           <surveyTheme.Survey
                              printRef={componentRef}
                              surveyAlldata={surveyAlldata}
                              surveyData={surveyData}
                              surveyFetchError={surveyFetchError}
                              hasErrors={hasErrors}
                              setHasErrors={setHasErrors}
                              submissionId={submissionId}
                              isArabic={shouldAddArabicClass}
                              showSurveyActions={showSurveyActions}
                              actionProps={actionProps}
                           />
                        ) : (
                           <>
                              <div
                                 className={`${shouldAddArabicClass ? "arabic-survey" : ""}`}
                                 style={{
                                    border: "1px solid var(--sc-line, #9BACEB)",
                                    borderRadius: "8px",
                                    overflow: "hidden"
                                 }}
                              >
                                 <PreviewLanguage
                                    ref={componentRef}
                                    surveyAlldata={surveyAlldata}
                                 />

                                 {surveyData?.length ? (
                                    <SurveyDataItems
                                       hasErrors={hasErrors}
                                       submissionId={submissionId}
                                       surveyData={surveyData}
                                       shouldAddArabicClass={shouldAddArabicClass}
                                    />
                                 ) : surveyFetchError ? (
                                    <Alert
                                       type="error"
                                       showIcon
                                       style={{
                                          margin: "50px 24px"
                                       }}
                                       message="Failed to load the survey"
                                       description={surveyFetchError}
                                    />
                                 ) : (
                                    <Empty
                                       style={{
                                          padding: "50px 0"
                                       }}
                                    />
                                 )}
                              </div>

                              {showSurveyActions && <PreviewActions {...actionProps} />}
                           </>
                        )}
                     </div>
                  )}
               </div>

               {/* Drafts modal - START */}
               <PreviewModal
                  width={922}
                  isModalOpen={!hasSurveyExpired && isModalOpen && surveyAlldata?.canSubmitSurvey}
                  setIsModalOpen={setIsModalOpen}
                  title="Saved drafts"
                  titleStyle={{
                     textAlign: "center",
                     fontSize: "21px",
                     fontWeight: 600
                  }}
                  footer={null}
               >
                  <div className="show-drafts-modal">
                     <p className="show-drafts-modal--description">
                        Below is the list of all your saved drafts. They will be deleted after 30
                        days if not submitted.
                        {/* TODO: write better description. 30 days bit is incorrect */}
                     </p>
                     <div>
                        <DraftList
                           selectedDraft={selectedDraft}
                           onDiscardDraft={discardDraft}
                           onDraftSelect={async (draftId) => {
                              if (draftId === START_NEW_DRAFT.draftId) {
                                 setSelectedDraft(null);
                                 setHasErrors(false);
                                 resetInputs();
                                 draftFormik.resetForm();
                              } else {
                                 const draftData = await loadDraft(id, draftId);
                                 if (draftData) {
                                    draftFormik.setValues({
                                       draftTitle: draftData.draftTitle,
                                       draftDesc: draftData.draftDescription
                                    });
                                 }
                                 setSelectedDraft(draftId);
                                 setDraftSurvey(id, draftId);
                              }
                              setIsModalOpen(false);
                           }}
                           onDraftSubmit={async (draftId) => {
                              const draftData = await loadDraft(id, draftId);

                              const result = await onSubmit(
                                 draftId,
                                 draftData.formData,
                                 true,
                                 draftData.renderedElementIds
                              );

                              reportSkippedDrafts([draftData], [result]);
                           }}
                           submittingAllDrafts={submittingAllDrafts}
                           handleSubmittingAllDrafts={handleSubmittingAllDrafts}
                        />
                     </div>

                     <div className="show-drafts-modal--form---actions">
                        <Button
                           prefixCls="show-drafts-modal--form---actions_cancel"
                           type="default"
                           onClick={() => {
                              setIsModalOpen(false);
                           }}
                        >
                           Cancel
                        </Button>
                        {(!isOnline && taskId) || receiverId ? null : (
                           <Button
                              prefixCls="show-drafts-modal--form---actions_save"
                              htmlType="submit"
                              type="primary"
                              loading={submittingAllDrafts}
                              disabled={draftCount <= 0}
                              onClick={async () => {
                                 try {
                                    setSubmittingAllDrafts(true);
                                    const surveyDrafts = await getAllDraftsForSurvey(id);
                                    const inputsMap = surveyDrafts.map(
                                       ({ draftId, formData, renderedElementIds }) => [
                                          draftId,
                                          formData,
                                          true,
                                          renderedElementIds
                                       ]
                                    );

                                    if (Boolean(taskId)) {
                                       const limit = Math.min(
                                          inputsMap.length,
                                          assignedTaskIdsForSurvey.length
                                       );

                                       const results = await Promise.all(
                                          inputsMap.slice(0, limit).map((args, index) => {
                                             const currentTaskId = assignedTaskIdsForSurvey[index];
                                             const modifiedArgs = [...args, currentTaskId];
                                             return onSubmit(...modifiedArgs);
                                          })
                                       );

                                       reportSkippedDrafts(surveyDrafts.slice(0, limit), results);
                                       navigate("/mytasks");
                                    } else {
                                       const results = await Promise.all(
                                          inputsMap.map(async (args) => await onSubmit(...args))
                                       );

                                       reportSkippedDrafts(surveyDrafts, results);
                                    }

                                    setSubmittingAllDrafts(false);
                                 } catch (err) {
                                    console.error(err);
                                    setSubmittingAllDrafts(false);
                                 }
                              }}
                           >
                              <SendOutlined
                                 style={{
                                    translate: "0 -5px"
                                 }}
                                 rotate={-45}
                              />
                              <span>Submit all drafts</span>
                           </Button>
                        )}
                     </div>
                  </div>
               </PreviewModal>
               {/* Drafts modal - END */}

               {/* Save draft modal - START */}
               <PreviewModal
                  isModalOpen={isSaveAsDraft}
                  setIsModalOpen={setIsSaveAsDraft}
                  title="Save as a draft"
                  titleStyle={{
                     textAlign: "center"
                  }}
                  footer={null}
               >
                  <div className="save-draft-modal">
                     <p className="save-draft-modal--description">
                        To keep a draft of this survey, assign a name to it to review it later.{" "}
                        <br />
                        If they are not submitted within <b>30 days</b> they will be permanently
                        deleted.
                        {/* TODO: write better description. 30 days bit is incorrect */}
                     </p>
                     <form
                        onSubmit={(e) => {
                           e.preventDefault();
                           onSaveAsDraft();
                        }}
                        className="save-draft-modal--form"
                     >
                        <div>
                           <div className="save-draft-modal--form---control">
                              <label htmlFor="draftTitle">
                                 <span
                                    style={{
                                       color: "#FF4D4F"
                                    }}
                                 >
                                    *
                                 </span>{" "}
                                 Draft name
                              </label>
                              <Input
                                 id="draftTitle"
                                 required
                                 type="text"
                                 placeholder="Type something here..."
                                 value={draftFormik.values.draftTitle}
                                 onChange={draftFormik.handleChange}
                              />
                           </div>
                           <div className="save-draft-modal--form---control">
                              <label
                                 htmlFor="draftDesc"
                                 style={{
                                    marginTop: "32px"
                                 }}
                              >
                                 Note{" "}
                                 <span
                                    style={{
                                       color: "#979797",
                                       fontSize: "12px",
                                       fontWeight: 400
                                    }}
                                 >
                                    (optional)
                                 </span>
                              </label>
                              <Input.TextArea
                                 id="draftDesc"
                                 showCount
                                 maxLength={100}
                                 autoSize={{ minRows: 2 }}
                                 placeholder="Autosize height based on content lines"
                                 value={draftFormik.values.draftDesc}
                                 onChange={draftFormik.handleChange}
                              />
                           </div>
                        </div>
                        <div className="save-draft-modal--form---actions">
                           <Button
                              prefixCls="save-draft-modal--form---actions_cancel"
                              type="default"
                              onClick={() => {
                                 setIsSaveAsDraft(false);
                                 draftFormik.resetForm();
                              }}
                           >
                              Cancel
                           </Button>
                           <Button
                              prefixCls="save-draft-modal--form---actions_save"
                              htmlType="submit"
                              type="primary"
                           >
                              Save and close
                           </Button>
                        </div>
                     </form>
                  </div>
               </PreviewModal>
               {/* Save draft modal - END */}

               {/* Survey expired modal - START */}
               <PreviewModal
                  isModalOpen={Boolean(hasSurveyExpired && surveyAlldata?.endDate)}
                  closable={isPublicSurveyRoute() ? false : true}
                  setIsModalOpen={() => {
                     if (!isPublicSurveyRoute()) {
                        setHasSurveyExpired(false);
                     }
                  }}
                  title="Survey expired"
                  titleStyle={{
                     textAlign: "center"
                  }}
                  footer={null}
               >
                  {/* we are borrowing the styles from drafts modal */}
                  <div>
                     <h5 className="show-drafts-modal--description">
                        Deadline: {dayjs(surveyAlldata?.endDate).format("MMMM Do, YYYY")}
                     </h5>
                     <p className="show-drafts-modal--description">
                        The survey has expired and can no longer be submitted. Please contact the
                        survey creator for more information.
                     </p>
                  </div>
               </PreviewModal>
               {/* Survey expired modal - END */}

               <>{modalContextHolder}</>
            </>
         )}
      </ThemeRoot>
   );
}

export default withErrorBoundary(Preview);
